import { FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Classifier, IndustryCodeFilter } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';
import { TreeItemRaw } from '../../Inputs/TreeComponent/Tree.types';
import TreeInput from '../../Inputs/TreeComponent/TreeInput';

interface Props {
  root: boolean;
  onChange: (value: IndustryCodeFilter) => void;
  defaultValue?: IndustryCodeFilter;
  items?: Classifier[] | TreeItemRaw[];
  active: boolean;
}

function modifyClassifiers(level: number, items: Classifier[]): any[] {
  return items.map((item) => {
    if (!item.id) {
      throw new Error('Item id is undefined');
    }
    if (item.values) {
      return {
        ...item,
        id: item.id + '_' + level,
        values: item.values,
      };
    }
    return { id: item.id, name: item.name || '' };
  });
}

const TreeWithExclusionFilterInput: React.FC<Props> = React.memo(
  ({
    onChange,
    root,
    active,
    defaultValue = {
      value: {
        tags: {
          includeValues: [],
          excludeValues: [],
        },
      },
      isPrimaryOnly: false,
    },
    items = [],
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const transformedItems = useMemo(() => {
      return modifyClassifiers(0, items);
    }, [items]);
    const [state, setState] = useState<IndustryCodeFilter>(defaultValue);
    const [toggleOpenState, setToggleOpenState] = useState<boolean>(false);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const rs: any[] = [];
    if (state.isPrimaryOnly) {
      rs.push({ label: 'Only primary' });
    }
    if (state.value?.tags?.includeValues?.length) {
      rs.push({ label: 'Included:', value: state.value.tags.includeValues.join(', ') });
    }
    if (state.value?.tags?.excludeValues?.length) {
      rs.push({ label: 'NOT included:', value: state.value.tags.excludeValues.join(', ') });
    }

    return (
      <>
        {active ? (
          <>
            <SwitchCompaniesUnknowns
              onChange={(value) => {
                setState({ ...state, isPrimaryOnly: value });
              }}
              checked={state.isPrimaryOnly}
              unknownSwitchLabel={t('companies:filterInputs.industryCodeIsPrimary')}
            />
            <FormControl className={`form-control ${classes.root}`}>
              <TreeInput
                items={transformedItems as TreeItemRaw[]}
                onChange={(includeValues) => {
                  setState({
                    ...state,
                    value: {
                      tags: {
                        includeValues,
                        excludeValues:
                          state.value && state.value.tags ? state.value.tags.excludeValues : [],
                      },
                    },
                  });
                }}
                defaultValue={state.value?.tags?.includeValues}
                chunkLoading={true}
              />
              <div
                className={classes.tagToggle}
                onClick={() => setToggleOpenState(!toggleOpenState)}
              >
                <FormHelperText>{t('app:notIncluded')}</FormHelperText>
                {toggleOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
              </div>
              {toggleOpenState ? (
                <TreeInput
                  items={transformedItems as TreeItemRaw[]}
                  onChange={(excludeValues) => {
                    setState({
                      ...state,
                      value: {
                        tags: {
                          excludeValues,
                          includeValues:
                            state.value && state.value.tags ? state.value.tags.includeValues : [],
                        },
                      },
                    });
                  }}
                  defaultValue={state.value?.tags?.excludeValues}
                  chunkLoading={true}
                />
              ) : null}
            </FormControl>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  root: {
    '.Mui-expanded': {
      margin: 0,
    },
  },
  tagToggle: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: theme.typography.fontSize - 2,
  },
}));

export default TreeWithExclusionFilterInput;
